import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useConnectivity } from '../hooks/useConnectivity';
import { useGeofence } from '../hooks/useGeofence';
import { useSip } from '../hooks/useSip';
import { useApi } from '../hooks/useApi';
import { useTranslation } from '../hooks/useTranslation';
import { dateLocale } from '../i18n';
import { useDisplaySettings, type ClockSize } from '../hooks/useDisplaySettings';
import { StatusIndicator } from '../components/StatusIndicator';
import { HomeFavourites } from '../components/HomeFavourites';
import { PhotoFrame } from '../components/PhotoFrame';
import { AnnouncementBanner } from '../components/AnnouncementBanner';
import { useUnreadNotificationCount } from '../hooks/useResidentNotifications';
import { AnalogClock } from '../components/AnalogClock';
import { getTimeOfDayGreeting } from '@caro/shared/utils';
import { now, clockParts, formatClockTime, formatClockDate } from '../utils/clock';
import { BuildingLogoChip } from '@caro/shared/components';
import { FeatureGate } from '@caro/shared/features';
import { API_ENDPOINTS } from '@caro/shared/constants';
import type { Announcement, BuildingBranding } from '@caro/shared/types';
import { SYSTEM_FONT_STACK } from '../constants/defaults';
import { clockFontSize, fitAnalogClock } from '../constants/standbyStyle';

// Home-screen clock sizes. The clock is a primary left-column element and has its
// OWN size setting (`clockSize`), independent of the app-wide font size, so it can
// grow past the largest text size. `medium` matches the old default (xlarge font:
// 64px × 1.6) so devices that never touch the setting look the same.
const HOME_CLOCK_ANALOG_SIZE_PX: Record<ClockSize, number> = {
  small: 150,
  medium: 210,
  large: 280,
  xlarge: 340,
  huge: 400,
};
const HOME_CLOCK_DIGITAL_SIZE_PX: Record<ClockSize, number> = {
  small: 76,
  medium: 104,
  large: 140,
  xlarge: 180,
  huge: 230,
};

export function HomePage() {
  const { t, lang } = useTranslation();
  const locale = dateLocale(lang);
  const navigate = useNavigate();
  const { user, api } = useAuth();
  const { overallStatus } = useConnectivity();
  const { config: geofenceConfig, isTracking, isInsideFence, startTracking } = useGeofence();
  useSip(); // Auto-connects on mount (singleton)
  const { settings } = useDisplaySettings();
  // Ongelezen bewonersmeldingen. De store draait op moduleniveau en pollt zelf; deze hook
  // leest alleen. Telt bewust GEEN `Announcement`-rijen mee: die hebben geen gelezen-status,
  // dus zouden de badge permanent laten branden — en een badge die altijd aan staat is geen
  // badge meer.
  const unreadNotifications = useUnreadNotificationCount();
  const [currentTime, setCurrentTime] = useState(now);

  // R5 / T050: front-page Open-door button. The tablet sends NO body params —
  // the backend in-process proxy derives the unit door number server-side from
  // the DeviceToken, so a tablet can only ever open its own unit's door
  // (plan §5 R5). Gated behind the `openDoor` feature flag via <FeatureGate>.
  const [doorState, setDoorState] = useState<'idle' | 'opening' | 'success' | 'error'>('idle');
  const doorResetRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const openDoor = useCallback(async () => {
    if (doorState === 'opening') return;
    if (doorResetRef.current) clearTimeout(doorResetRef.current);
    setDoorState('opening');
    try {
      await api.post(API_ENDPOINTS.OPEN_DOOR);
      setDoorState('success');
    } catch {
      setDoorState('error');
    } finally {
      doorResetRef.current = setTimeout(() => setDoorState('idle'), 4000);
    }
  }, [api, doorState]);
  useEffect(() => () => {
    if (doorResetRef.current) clearTimeout(doorResetRef.current);
  }, []);

  // NOTE (T1.3 / Phase 1): useBranding was hoisted to App.tsx so that branding
  // tokens persist across navigation (Home → Settings → Photos → ...). Do not
  // re-add it here.

  // Fetch announcements
  const { data: announcements } = useApi<Announcement[]>(
    user?.tenantId ? `/api/v1/building/${user.tenantId}/announcements` : null
  );

  // Fetch branding to display the building logo chip next to the greeting.
  const { data: configResp } = useApi<{ branding?: BuildingBranding; name?: string }>(
    user?.tenantId ? `/api/v1/building/${user.tenantId}/config` : null
  );
  const buildingBranding = configResp?.branding;
  const buildingName = configResp?.name;

  // Update time — every second for analog clock, every minute for digital
  useEffect(() => {
    const ms = settings.clockStyle === 'analog' ? 1_000 : 60_000;
    const interval = setInterval(() => setCurrentTime(now()), ms);
    return () => clearInterval(interval);
  }, [settings.clockStyle]);

  // Auto-start geofence tracking when config is available
  useEffect(() => {
    if (geofenceConfig && !isTracking) {
      startTracking();
    }
  }, [geofenceConfig, isTracking, startTracking]);

  const greeting = getTimeOfDayGreeting(clockParts(currentTime).hour);
  const greetingText = {
    morning: t.home.greetingMorning,
    afternoon: t.home.greetingAfternoon,
    evening: t.home.greetingEvening,
    night: t.home.greetingNight,
  }[greeting];

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100vh',
      overflow: 'hidden',
      padding: '24px',
      position: 'relative',
    }}>
      {/* Photo frame background (behind content) */}
      <PhotoFrame />

      {/* Top bar: connection status (→ connectivity) + greeting/logo. The
          edit-layout pencil was removed — the home layout is now fixed. */}
      <header style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '16px',
        position: 'relative',
        zIndex: 1,
        gap: '12px',
        flexWrap: 'wrap',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <StatusIndicator status={overallStatus} />
          {geofenceConfig && isTracking && (
            <span
              title={isInsideFence === false ? t.home.geofenceOutside : t.home.geofenceInside}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                padding: '4px 8px',
                borderRadius: '8px',
                fontSize: 'var(--font-small)',
                fontWeight: 600,
                backgroundColor: isInsideFence === false ? '#FEE2E2' : '#DCFCE7',
                color: isInsideFence === false ? '#DC2626' : '#16A34A',
              }}
            >
              {isInsideFence === false ? `⚠ ${t.home.geofenceIndicatorOutside}` : `📍 ${t.home.geofenceIndicatorSafe}`}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <BuildingLogoChip
            logoUrl={buildingBranding?.logo}
            buildingName={buildingName}
            maxHeight={44}
          />
        </div>
      </header>

      {/* Announcements (renders nothing when there are none) */}
      <div style={{ position: 'relative', zIndex: 1 }}>
        <AnnouncementBanner announcements={announcements || []} />
      </div>

      {/* Berichten-tegel. Verschijnt ALLEEN wanneer er iets ongelezen is: een knop die er
          altijd staat wordt achtergrond, en dan valt het niet meer op wanneer er wél iets
          is. Kleuren via de thematokens (nooit een hex — zie AnnouncementBanner), touch-
          target ruim boven de 48px-norm. */}
      {unreadNotifications > 0 && (
        <div style={{ position: 'relative', zIndex: 1, marginBottom: '12px' }}>
          <button
            type="button"
            onClick={() => navigate('/notifications')}
            aria-label={`${unreadNotifications} ongelezen ${unreadNotifications === 1 ? 'bericht' : 'berichten'} openen`}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              width: '100%',
              minHeight: '64px',
              padding: '12px 16px',
              borderStyle: 'solid',
              borderColor: 'var(--caro-border)',
              borderWidth: '1px',
              borderLeftWidth: '6px',
              borderLeftColor: 'var(--caro-primary)',
              borderRadius: '12px',
              backgroundColor: 'var(--caro-card-bg)',
              color: 'var(--caro-text)',
              fontSize: 'var(--font-body)',
              textAlign: 'left',
              cursor: 'pointer',
              boxShadow: 'var(--caro-card-shadow, none)',
            }}
          >
            <span
              aria-hidden="true"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: '40px',
                height: '40px',
                borderRadius: '20px',
                backgroundColor: 'var(--caro-primary)',
                color: 'var(--caro-card-bg)',
                fontWeight: 700,
                fontSize: '20px',
              }}
            >
              {unreadNotifications}
            </span>
            {/* Het getal staat in de bol, maar het WOORD staat er ook: een cijfer alleen
                zegt een oudere lezer niet waar het over gaat. */}
            <span style={{ fontWeight: 600 }}>
              {unreadNotifications === 1 ? 'Nieuw bericht' : 'Nieuwe berichten'}
            </span>
          </button>
        </div>
      )}

      {/* Main two-column layout:
          LEFT  — clock (top) + greeting, and a big "Apps" launcher below.
          RIGHT — large emergency button (≈¾ of the height, taller than wide)
                  with the half-width open-door button beneath it. */}
      <div style={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        gap: '24px',
        position: 'relative',
        zIndex: 1,
        marginTop: '16px',
      }}>
        {/* LEFT COLUMN — clock only, centered, no background. The Apps
            launcher is intentionally hidden for this stage. */}
        <div style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          minHeight: 0,
        }}>
            {settings.clockStyle === 'analog' ? (
              <>
                <AnalogClock
                  size={fitAnalogClock(HOME_CLOCK_ANALOG_SIZE_PX[settings.clockSize], 0.55)}
                  color="white"
                  bold={settings.clockBold}
                />
                <div style={{
                  fontFamily: 'var(--caro-font-display)',
                  fontSize: 'var(--font-heading)',
                  fontWeight: settings.clockBold ? 700 : 400,
                  color: 'rgba(255,255,255,0.9)',
                  textShadow: '0 1px 4px rgba(0,0,0,0.6)',
                  marginTop: '8px',
                }}>
                  {formatClockDate(currentTime, locale, { weekday: 'long', day: 'numeric', month: 'long' })}
                </div>
              </>
            ) : (
              <>
                <div style={{
                  fontFamily: 'var(--caro-font-display)',
                  // The left column is half the screen: cap at 17vw so "12:34" always fits.
                  fontSize: clockFontSize(HOME_CLOCK_DIGITAL_SIZE_PX[settings.clockSize], 17, 40),
                  fontWeight: settings.clockBold ? 900 : 700,
                  color: 'white',
                  lineHeight: 1,
                  textShadow: '0 2px 8px rgba(0,0,0,0.6)',
                  fontVariantNumeric: 'tabular-nums',
                }}>
                  {formatClockTime(currentTime, locale)}
                </div>
                <div style={{
                  fontFamily: 'var(--caro-font-display)',
                  fontSize: 'var(--font-heading)',
                  fontWeight: settings.clockBold ? 700 : 400,
                  color: 'rgba(255,255,255,0.9)',
                  textShadow: '0 1px 4px rgba(0,0,0,0.6)',
                }}>
                  {formatClockDate(currentTime, locale, { weekday: 'long', day: 'numeric', month: 'long' })}
                </div>
              </>
            )}
            <div style={{
              fontSize: 'var(--font-body)',
              fontWeight: 600,
              color: 'rgba(255,255,255,0.95)',
              textShadow: '0 1px 4px rgba(0,0,0,0.6)',
              marginTop: '8px',
              textAlign: 'center',
            }}>
              {greetingText}, {user?.firstName || 'Resident'}
            </div>

            {/*
              ── AGENDA ────────────────────────────────────────────────────────
              Same treatment as Domotica: a feature-gated glass tile that opens a real
              page. Gated on `agenda`, which resolves down the LOCATION chain
              (site → building → floor → unit) — the tablet hangs in the room, so the
              room decides. A unit may be opened while its site stays closed, which is
              what makes a per-flat rollout possible.

              ── WHY THE LEFT COLUMN AND NOT BESIDE DOMOTICA ───────────────────
              The right column is emergency (flex 3) over openDoor and Domotica (flex 1
              each). A fourth tile there would drop the emergency button from ~60% to
              ~50% of the column height, and on an 800×480 panel three 96px tiles plus a
              200px emergency plus gaps do not fit. Shrinking the emergency button is a
              decision for the field, not a side effect of adding an agenda.

              The left column held only a clock — the Apps launcher was deliberately
              hidden — so the agenda takes the empty half. That also makes it prominent
              rather than the smallest thing on the screen, which is the point: this is
              the surface residents are meant to start using.

              With the flag off this renders NOTHING and the column is the clock exactly
              as it is today. The interface changes only where the agenda was opened.
            */}
            <FeatureGate feature="agenda">
              <button
                type="button"
                data-testid="home-agenda"
                onClick={() => navigate('/agenda')}
                aria-label={t.agenda?.title || 'Agenda'}
                style={{
                  marginTop: '20px',
                  alignSelf: 'stretch',
                  // 96px matches the other home tiles; this one commits a resident to
                  // nothing, so it does not need the 80px emergency-grade minimum for
                  // its own sake — it needs to match its neighbours.
                  minHeight: '96px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '16px',
                  borderRadius: '28px',
                  background:
                    'linear-gradient(135deg, rgba(255,255,255,0.28) 0%, rgba(255,255,255,0.08) 32%, rgba(255,255,255,0) 60%), '
                    + 'var(--caro-home-tile-tint, color-mix(in srgb, var(--caro-primary) 45%, transparent))',
                  backdropFilter: 'blur(24px) saturate(180%)',
                  WebkitBackdropFilter: 'blur(24px) saturate(180%)',
                  border: 'var(--caro-home-tile-border, 1px solid rgba(255,255,255,0.3))',
                  color: 'white',
                  fontWeight: 700,
                  textShadow: '0 1px 6px rgba(0,0,0,0.45)',
                  cursor: 'pointer',
                  boxShadow:
                    'inset 0 1px 0 rgba(255,255,255,0.5), inset 0 -2px 8px rgba(0,0,0,0.28), 0 10px 32px rgba(0,0,0,0.32)',
                }}
              >
                <span aria-hidden="true" style={{ fontSize: 'calc(var(--font-datetime) * 1.1)', lineHeight: 1 }}>🗓️</span>
                <span style={{ fontSize: 'var(--font-heading)' }}>{t.agenda?.title || 'Agenda'}</span>
              </button>
            </FeatureGate>
        </div>

        {/* RIGHT COLUMN — emergency (≈¾) over door-open */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '24px', minHeight: 0 }}>
          <button
            type="button"
            data-testid="home-emergency"
            onClick={() => navigate('/emergency')}
            aria-label={t.emergency?.callHelp || 'Call help'}
            style={{
              flex: 3,
              minHeight: '200px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '16px',
              borderRadius: '32px',
              // Liquid-glass material: a translucent red tint with a diagonal
              // specular sheen on top, frosted backdrop blur, a bright top edge
              // and layered depth shadows.
              background:
                'linear-gradient(135deg, rgba(255,255,255,0.28) 0%, rgba(255,255,255,0.08) 32%, rgba(255,255,255,0) 60%), rgba(61, 1, 0, 0.62)',
              backdropFilter: 'blur(24px) saturate(180%)',
              WebkitBackdropFilter: 'blur(24px) saturate(180%)',
              border: '1px solid rgba(255,255,255,0.3)',
              color: 'white',
              cursor: 'pointer',
              textShadow: '0 1px 6px rgba(0,0,0,0.45)',
              boxShadow:
                'inset 0 1px 0 rgba(255,255,255,0.5), inset 0 -2px 8px rgba(0,0,0,0.28), 0 12px 40px rgba(61,1,0,0.45)',
              // The emergency tile keeps the system font under every theme.
              fontFamily: SYSTEM_FONT_STACK,
            }}
          >
            <span aria-hidden="true" style={{ fontSize: 'calc(var(--font-datetime) * 2)', lineHeight: 1 }}>{'\u{1F4DE}'}</span>
            <span style={{ fontSize: 'var(--font-datetime)', fontWeight: 800, textAlign: 'center', padding: '0 12px' }}>
              {t.emergency?.callHelp || 'Call help'}
            </span>
          </button>

          {/* R5 / T050: Open-door button — bottom-right, gated behind `openDoor`.
              Sits at flex:1 under the emergency button (≈¼ height). */}
          <FeatureGate feature="openDoor">
            <button
              type="button"
              data-testid="home-open-door"
              onClick={openDoor}
              disabled={doorState === 'opening'}
              aria-label={t.home.openDoor}
              style={{
                flex: 1,
                minHeight: '96px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '16px',
                borderRadius: '28px',
                // Liquid-glass material — same sheen + frosted blur as the
                // emergency button, with the door state color as the tint.
                background:
                  'linear-gradient(135deg, rgba(255,255,255,0.28) 0%, rgba(255,255,255,0.08) 32%, rgba(255,255,255,0) 60%), ' +
                  (doorState === 'success'
                    ? 'rgba(22, 163, 74, 0.6)'
                    : doorState === 'error'
                      ? 'rgba(220, 38, 38, 0.6)'
                      : 'var(--caro-home-tile-tint, color-mix(in srgb, var(--caro-primary) 62%, transparent))'),
                backdropFilter: 'blur(24px) saturate(180%)',
                WebkitBackdropFilter: 'blur(24px) saturate(180%)',
                border: 'var(--caro-home-tile-border, 1px solid rgba(255,255,255,0.3))',
                color: 'white',
                fontWeight: 700,
                textShadow: '0 1px 6px rgba(0,0,0,0.45)',
                cursor: doorState === 'opening' ? 'default' : 'pointer',
                opacity: doorState === 'opening' ? 0.7 : 1,
                boxShadow:
                  'inset 0 1px 0 rgba(255,255,255,0.5), inset 0 -2px 8px rgba(0,0,0,0.28), 0 10px 32px rgba(0,0,0,0.32)',
              }}
            >
              <span aria-hidden="true" style={{ fontSize: 'calc(var(--font-datetime) * 1.1)', lineHeight: 1 }}>🔓</span>
              <span style={{ fontSize: 'var(--font-heading)' }}>
                {doorState === 'success'
                  ? t.home.openDoorSuccess
                  : doorState === 'error'
                    ? t.home.openDoorError
                    : t.home.openDoor}
              </span>
            </button>
          </FeatureGate>

          {/* Domotics — gated on `domotics`, which is SITE-OPT-IN: a site must write an
              explicit `true` or this resolves false at every descendant. The gate here
              only hides the tile; every endpoint behind it re-enforces the same rule
              server-side, because a valid device token can reach them directly. */}
          <FeatureGate feature="domotics">
            <button
              type="button"
              data-testid="home-domotics"
              onClick={() => navigate('/domotics')}
              aria-label="Domotica"
              style={{
                flex: 1,
                minHeight: '96px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '16px',
                borderRadius: '28px',
                background:
                  'linear-gradient(135deg, rgba(255,255,255,0.28) 0%, rgba(255,255,255,0.08) 32%, rgba(255,255,255,0) 60%), '
                  + 'var(--caro-home-tile-tint, color-mix(in srgb, var(--caro-primary) 45%, transparent))',
                backdropFilter: 'blur(24px) saturate(180%)',
                WebkitBackdropFilter: 'blur(24px) saturate(180%)',
                border: 'var(--caro-home-tile-border, 1px solid rgba(255,255,255,0.3))',
                color: 'white',
                fontWeight: 700,
                textShadow: '0 1px 6px rgba(0,0,0,0.45)',
                cursor: 'pointer',
                boxShadow:
                  'inset 0 1px 0 rgba(255,255,255,0.5), inset 0 -2px 8px rgba(0,0,0,0.28), 0 10px 32px rgba(0,0,0,0.32)',
              }}
            >
              <span aria-hidden="true" style={{ fontSize: 'calc(var(--font-datetime) * 1.1)', lineHeight: 1 }}>💡</span>
              <span style={{ fontSize: 'var(--font-heading)' }}>Domotica</span>
            </button>
          </FeatureGate>

          {/* De lampen en sferen die de bewoner zelf op zijn beginscherm heeft gezet.
              Rendert niets wanneer hij er geen heeft, dus de kolom blijft dan precies
              zoals hij was — de noodknop mag geen millimeter inleveren voor een strook
              die leeg is.

              Ook gated op `domotics`: zonder die feature bestaan er geen favorieten om
              te tonen, en de server zou ze sowieso weigeren. */}
          <FeatureGate feature="domotics">
            <HomeFavourites />
          </FeatureGate>
        </div>
      </div>
    </div>
  );
}
